import { Lightning, Registry } from '@lightningjs/sdk';
import AbstractPlayerEventHandler from './AbstractPlayerEventHandler';
import { EpgChannel } from 'types/api/media';
import { PlaybackEvent, ViewContext } from 'types/analytics';
import {
  getCurrentEpgProgram,
  getEpgProgramElapsedSeconds,
} from 'support/contentUtils';
import { setJitterTimeout } from 'support/playerUtils';
import {
  reportContentMetadata,
  reportEpgProgramSwitch,
  reportPlayback,
} from 'services/analytics/reportingServiceVideo';

export default class EpgPlayerEventHandler
  extends AbstractPlayerEventHandler
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractPlayerEventHandler>
{
  private _channel: EpgChannel | null = null;
  private _epgProgramUpdateTimeoutId: number | null = null;
  private _pendingProgramEnd: (() => void) | undefined = undefined;
  private _pendingProgramEndTime: number | null = null;

  override initializePlayerContent(
    mediaContent: EpgChannel,
    viewContext: ViewContext | null,
  ) {
    this._channel = mediaContent;

    this.clearProgramUpdateTimeout();
    this.setEpgProgramUpdates(mediaContent, viewContext);
  }

  override resetAnalytics() {
    super.resetAnalytics();
    this.clearProgramUpdateTimeout();
  }

  override handlePlayerStart(rawTime: number, isEpgUpdate?: boolean) {
    if (!this._Player || (this._hasStarted && !isEpgUpdate)) {
      return;
    }

    this.reportPlayerStart(rawTime);
    if (!isEpgUpdate) {
      this.startAnalytics();
    }
    this._hasStarted = true;
  }

  override getStartRelativeTime(): number {
    return getEpgProgramElapsedSeconds(this._channel as EpgChannel);
  }

  override handlePlayerEnd(args: { hasReachedEnd: boolean }) {
    // The program update is staggered by a jitter, so leaving the EPG (changing
    // channels or navigating away) would otherwise drop the program end reporting.
    this.flushPendingProgramEnd();

    super.handlePlayerEnd(args);
  }

  // automatically updates EPG program
  private setEpgProgramUpdates(
    channel: EpgChannel,
    viewContext: ViewContext | null,
  ) {
    this.clearProgramUpdateTimeout();

    const program = getCurrentEpgProgram(channel);
    if (!program?.endTime) return;

    const programEndTime = new Date(program.endTime).valueOf();
    const delay = programEndTime - new Date().valueOf();

    this._pendingProgramEndTime = programEndTime;
    this._pendingProgramEnd = () => {
      // For EPG, we only report finished when the program finishes.
      this.reportProgramEnded();
    };

    this._epgProgramUpdateTimeoutId = setJitterTimeout(() => {
      const programEnd = this._pendingProgramEnd;

      reportEpgProgramSwitch(channel, viewContext);

      programEnd?.();

      // Cleared before running so the update can safely re-arm the next timer
      this._epgProgramUpdateTimeoutId = null;
      this._pendingProgramEnd = undefined;
      this._pendingProgramEndTime = null;

      // Set new program
      reportContentMetadata({
        mediaContent: channel,
        fromPageId: this._fromPageId,
        viewContext,
      });
      this.handlePlayerStart(0, true);
      this.setEpgProgramUpdates(channel, viewContext);
    }, delay);
  }

  /**
   * Reports the program update immediately if the program has already ended but
   * the staggered timer hasn't fired yet. If the program is still playing the
   * pending update is left alone.
   */
  private flushPendingProgramEnd() {
    if (
      !this._isInitialized ||
      this._epgProgramUpdateTimeoutId === null ||
      this._pendingProgramEndTime === null ||
      Date.now() < this._pendingProgramEndTime
    ) {
      return;
    }

    const programEnd = this._pendingProgramEnd;
    this.clearProgramUpdateTimeout();

    programEnd?.();
  }

  private clearProgramUpdateTimeout() {
    if (this._epgProgramUpdateTimeoutId !== null) {
      Registry.clearTimeout(this._epgProgramUpdateTimeoutId);
    }
    this._epgProgramUpdateTimeoutId = null;
    this._pendingProgramEnd = undefined;
    this._pendingProgramEndTime = null;
  }

  private reportProgramEnded() {
    if (!this._isInitialized) return;

    this._hasStarted = false;

    const relativeTime = this._Player!.currentContentTime();
    const rawTime = this._Player!.currentRawTime();

    // For program update, keep the player and heartbeat active.
    // Only clear the current program timer.
    this.clearProgramUpdateTimeout();

    reportPlayback(
      PlaybackEvent.END,
      { relativeTime, rawTime },
      {
        hasEnded: true,
        isAdPlaying: this._isAdPlaying,
        isEpgUpdate: true,
      },
    );
  }

  /* =============================
   * Events
   * ============================= */

  override $videoPlayerTimeUpdate(_rawTime: number) {
    // no-op
  }
  override $videoPlayerEnded(_rawTime: number) {
    // no-op
  }
  override $videoPlayerSeeking(_rawTime: number) {
    // no-op
  }
  override $videoPlayerSeeked(_rawTime: number) {
    // no-op
  }
  override $videoManagerBeforeSeek() {
    // no-op
  }
  override $videoManagerSeekPause() {
    // no-op
  }
  override $videoPlayerUnloading() {
    // no-op
  }
}
